'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Send, MapPin, Mail, Phone, Clock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';


interface ContactForm {
  name: string;
  email: string;
  company: string;
  service: string;
  budget: string;
  message: string;
}

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactForm>();

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('website_inquiries').insert({
        name: data.name,
        email: data.email,
        company: data.company || null,
        service_type: data.service || null,
        budget_range: data.budget || null,
        project_description: data.message,
      });

      if (error) {
        toast.error('Failed to send message. Please try again.');
        console.error('Supabase insert error:', error.message);
      } else {
        reset();
        toast.success("Message sent! We'll respond within 24 hours.");
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
      console.error('Unexpected error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 aurora-bg opacity-40" />
      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-14">
          <span className="section-label">Get In Touch</span>
          <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
            Let&apos;s Build Something{' '}
            <span className="text-gradient-primary">Remarkable</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            Tell us about your project. We&apos;ll get back to you within 24 hours with a tailored proposal.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          {/* Contact info */}
          <div className="xl:col-span-2 space-y-5">
            {[
              { id: 'ci-location', icon: MapPin, label: 'Office', value: 'Ward No 18, Chainpura\nPiprali, Sikar' },
              { id: 'ci-email', icon: Mail, label: 'Email', value: 'yogeshmahala77429@gmail.com' },
              { id: 'ci-phone', icon: Phone, label: 'Phone', value: '+91 90016 12303' },
              { id: 'ci-hours', icon: Clock, label: 'Business Hours', value: 'Mon–Fri, 9am–6pm IST\nEmergency support 24/7' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="glass-card rounded-2xl p-5 border border-border/40 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-mono-code text-muted-foreground/60 uppercase tracking-widest mb-1">{item.label}</p>
                    <p className="text-sm font-display font-medium text-foreground whitespace-pre-line">{item.value}</p>
                  </div>
                </div>
              );
            })}

            {/* Response time */}
            <div className="glass-card rounded-2xl p-5 border border-emerald/30 bg-emerald/5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-emerald animate-pulse" />
                <span className="text-xs font-mono-code text-emerald tracking-widest uppercase">Available Now</span>
              </div>
              <p className="text-sm text-muted-foreground">Average response time: <span className="text-foreground font-semibold">under 4 hours</span> during business hours.</p>
            </div>
          </div>

          {/* Contact form */}
          <div className="xl:col-span-3">
            <form onSubmit={handleSubmit(onSubmit)} className="glass-card rounded-2xl p-7 border border-border/40 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-display font-semibold text-foreground mb-1.5">
                    Full Name <span className="text-rose">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Marcus Chen"
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 transition-all duration-200"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <p className="text-xs text-rose mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-display font-semibold text-foreground mb-1.5">
                    Work Email <span className="text-rose">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="marcus@company.com"
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 transition-all duration-200"
                    {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
                  />
                  {errors.email && <p className="text-xs text-rose mt-1">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-display font-semibold text-foreground mb-1.5">Company</label>
                  <input
                    type="text"
                    placeholder="Nexus Corp"
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 transition-all duration-200"
                    {...register('company')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-display font-semibold text-foreground mb-1.5">Service Needed</label>
                  <select
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-xl text-sm text-foreground focus:outline-none focus:border-primary/60 transition-all duration-200"
                    {...register('service')}
                  >
                    <option value="">Select a service...</option>
                    <option value="web-design">Web Design</option>
                    <option value="web-dev">Web Development</option>
                    <option value="ecommerce">E-Commerce</option>
                    <option value="seo">SEO & Performance</option>
                    <option value="uiux">UI/UX Design</option>
                    <option value="branding">Branding</option>
                    <option value="mobile">Mobile App</option>
                    <option value="other">Other / Not Sure</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-display font-semibold text-foreground mb-1.5">Estimated Budget</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { id: 'budget-1', label: 'Under $2k', value: 'under-2k' },
                    { id: 'budget-2', label: '$2k–$5k', value: '2k-5k' },
                    { id: 'budget-3', label: '$5k–$15k', value: '5k-15k' },
                    { id: 'budget-4', label: '$15k+', value: '15k-plus' },
                  ].map((b) => (
                    <label key={b.id} className="cursor-pointer">
                      <input type="radio" value={b.value} className="sr-only" {...register('budget')} />
                      <span className="block text-center text-xs font-display font-medium px-3 py-2 rounded-lg border border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground transition-all duration-200 peer-checked:border-primary peer-checked:text-primary">
                        {b.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-display font-semibold text-foreground mb-1.5">
                  Project Details <span className="text-rose">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">Describe your project, goals, timeline, and any specific requirements.</p>
                <textarea
                  rows={5}
                  placeholder="We're looking to redesign our hotel website with online booking integration. The site currently gets 3,000 visitors/month and we want to increase direct bookings by 30%..."
                  className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 transition-all duration-200 resize-none"
                  {...register('message', { required: 'Project details are required' })}
                />
                {errors.message && <p className="text-xs text-rose mt-1">{errors.message.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center text-sm py-3 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending your message...
                  </>
                ) : (
                  <>
                    Send Message <Send size={14} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}